import React, { Fragment, useState } from "react";
import {
  string,
  func,
  bool,
  object,
  arrayOf,
  shape,
  number,
  any,
} from "prop-types";
import { connect } from "react-redux";

import moment from "moment";
import { compose, getComponentDisplayName, pick } from "../../helpers";
import {
  getToDate,
  workHistoryToUiModel,
  uiModelToWorkHistory,
  addIdToWorkHistory,
  loadWorkHistoryUi,
} from "../../helpers/workHistory";

import {
  onboardUpdateState,
  updateUserInProfileSilent,
  storeWorkHistory,
} from "../../redux/actions";

import { Loading, Backdrop, Validate, Button } from "../../common";
import * as style from "./styles.module.scss";

import {
  WorkHistoryForm,
  WorkHistoryCard,
} from "../../components/BasicInfo/WorkHistory";
import OnboardImage from "../../components/OnboardImage";

const defaultRoute = "/onboard/work-type";

const constraints = {
  fromMonth: {
    presence: { allowEmpty: false },
  },
  fromYear: {
    presence: { allowEmpty: false },
  },
  companyName: {
    presence: { allowEmpty: false },
  },
  toMonth: (value, attributes) => {
    if (!attributes.currentlyWorkHere) {
      return {
        presence: { allowEmpty: false },
      };
    }
    return {};
  },
  toYear: (value, attributes) => {
    if (!attributes.currentlyWorkHere) {
      return {
        presence: { allowEmpty: false },
      };
    }
    return {};
  },
  title: {
    presence: { allowEmpty: false },
  },
  description: {
    presence: { allowEmpty: true },
  },
};

const withWorkHistoryState = (Component) => {
  const wrappedComponent = (props) => {
    const initialState = {
      editingId: null,
      title: "",
      companyName: "",
      fromMonth: "",
      toMonth: "",
      fromYear: "",
      toYear: "",
      description: "",
      currentlyWorkHere: false,
      isEditing: false,
      showForm: false,
    };
    const [state, setState] = useState(initialState);
    const stateFunctions = {
      onChange: (key, val) => {
        if (key === "currentlyWorkHere" && val) {
          setState({
            ...state,
            [key]: val,
            toMonth: "",
            toYear: "",
          });
        } else {
          setState({ ...state, [key]: val });
        }
      },
      onEditClick: (workHistoryId) => {
        setState({
          ...state,
          ...loadWorkHistoryUi(props.workHistories, workHistoryId),
          isEditing: true,
          showForm: true,
          editingId: workHistoryId,
        });
      },
      clearForm: () => setState(initialState),
      toggleForm: (val) => setState({ ...state, showForm: val }),
    };
    return <Component {...props} {...state} {...stateFunctions} />;
  };
  wrappedComponent.displayName = `withWorkHistoryState(${getComponentDisplayName(
    Component
  )})`;
  wrappedComponent.propTypes = {
    workHistories: arrayOf(WorkHistoryCard.propTypes.workHistory).isRequired,
  };
  return wrappedComponent;
};

const withWorkHistoryProps = (Component) => {
  const wrappedComponent = (props) => {
    const {
      appData: { candidateRef, emailAddress },
      workHistories,
      editingId,
      saveWorkHistory,
      clearForm,
      toggleForm,
      updateUser,
      onboardState,
      ...workHistoryUiItem
    } = props;

    const updateWorkHistories = (updatedHistories, progressOnboarding) => {
      const updatedAppData = {
        candidateRef,
        emailAddress,
        workHistory: updatedHistories,
      };
      return updateUser(candidateRef, updatedAppData, progressOnboarding);
    };
    const workHistoryFunctions = {
      addWorkHistory: () => {
        const updatedWorkHistories = [
          uiModelToWorkHistory(workHistoryUiItem),
          ...workHistories,
        ];
        updateWorkHistories(updatedWorkHistories, "cv");
        clearForm();
      },
      editWorkHistory: () => {
        const updatedWorkHistories = workHistories.map((workHistory) =>
          workHistory.id === workHistoryUiItem.id
            ? uiModelToWorkHistory({ ...workHistoryUiItem, id: editingId })
            : workHistory
        );
        updateWorkHistories(updatedWorkHistories);
        clearForm();
      },
      removeWorkHistory: (removedId) => {
        const updatedAppData = workHistories.filter(
          ({ id }) => id !== removedId
        );
        updateWorkHistories(updatedAppData);
      },
    };
    return <Component {...props} {...workHistoryFunctions} />;
  };
  wrappedComponent.propTypes = {
    appData: shape({
      candidateRef: number.isRequired,
      emailAddress: string.isRequired,
    }),
    workHistories: arrayOf(WorkHistoryCard.propTypes.workHistory).isRequired,
    editingId: string,
    saveWorkHistory: func.isRequired,
    clearForm: func.isRequired,
    toggleForm: func.isRequired,
    updateUser: func.isRequired,
    onboardState: object.isRequired,
  };
  wrappedComponent.displayName = `withWorkHistoryProps(${getComponentDisplayName(
    Component
  )})`;
  return wrappedComponent;
};

const WorkHistory = ({
  appData,
  workHistories,
  routeWhenDone = defaultRoute,
  loading,
  onEditClick,
  addWorkHistory,
  editWorkHistory,
  removeWorkHistory,
  showForm,
  toggleForm,
  isOnboarding = false,
  isEditing,
  onboardStateUpdate,
  onboardState,
  ...props
}) => {
  const hasWorkHistories = workHistories && workHistories.length > 0;
  const showCards = !isEditing && hasWorkHistories;
  const isFirstTime = !showForm && !hasWorkHistories;
  const workHistoryInput = pick(props, [
    "title",
    "companyName",
    "fromMonth",
    "toMonth",
    "fromYear",
    "toYear",
    "currentlyWorkHere",
    "description",
  ]);

  const handleSubmit = () => onboardStateUpdate({ ...onboardState, cv: true });

  return (
    <Fragment>
      <Backdrop show={!appData}>
        <Loading />
      </Backdrop>
      <Fragment>
        <div className={`row h-100`}>
          <OnboardImage
            title="Great work!"
            subtitle="Now we just need your CV / work history"
            mobileDescription="Providing a CV or work history is really important to help us match you to the right opportunities."
            desktopDescription="Providing a CV or work history is really important to help us match you to the right opportunities."
            imagePath="/images/cv_info.svg"
            className={style.workHistory__imageBackground}
            imageClassName={style.workHistory__image}
          />
          <div
            style={{
              height: "100%",
              overflow: "auto",
              display: "flex",
              flexDirection: "column",
            }}
            className={`col-sm-12 col-md-12 col-lg-6 col-xl-6`}
          >
            {hasWorkHistories &&
              workHistories
                .sort((a, b) => moment(getToDate(b)).diff(getToDate(a)))
                .map(workHistoryToUiModel)
                .map((workHistory) => (
                  <WorkHistoryCard
                    key={workHistory.id}
                    workHistory={workHistory}
                    onEdit={onEditClick}
                    onRemove={removeWorkHistory}
                    includeButtons={!isOnboarding}
                  />
                ))}
            <Validate constraints={constraints} userInput={workHistoryInput}>
              {({ isValid, validation }) => (
                <Fragment>
                  <WorkHistoryForm
                    {...props}
                    validation={validation}
                    isValid={isValid}
                    onSave={isEditing ? editWorkHistory : addWorkHistory}
                    onboardStateUpdate={onboardStateUpdate}
                    onboardState={onboardState}
                  />
                </Fragment>
              )}
            </Validate>
          </div>
        </div>
      </Fragment>
    </Fragment>
  );
};

WorkHistory.propTypes = {
  appData: object.isRequired,
  workHistories: arrayOf(WorkHistoryCard.propTypes.workHistory).isRequired,
  routeWhenDone: string.isRequired,
  loading: bool.isRequired,
  onEditClick: func.isRequired,
  addWorkHistory: func.isRequired,
  editWorkHistory: func.isRequired,
  removeWorkHistory: func.isRequired,
  showForm: bool.isRequired,
  toggleForm: func.isRequired,
  isOnboarding: bool,
  isEditing: bool.isRequired,
  onboardStateUpdate: func,
  onboardState: any,
};

const mapStateToProps = ({ core }) => ({
  appData: core.app,
  workHistories: core.app.workHistory
    ? core.app.workHistory.map(addIdToWorkHistory)
    : [],
  onboardState: core.onboardState,
  loading: core.loading,
});

const mapDispatchToProps = {
  onboardStateUpdate: onboardUpdateState,
  updateUser: updateUserInProfileSilent,
  saveWorkHistory: storeWorkHistory,
};

const withRedux = connect(mapStateToProps, mapDispatchToProps);

const withApi = compose(withRedux, withWorkHistoryState, withWorkHistoryProps);

export default withApi(WorkHistory);
